import React, { useState, useEffect } from 'react';
import { Tooltip, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DashboardPage from '../layouts/DashboardPage';
import DeleteUserDialog from '../components/DeleteUserDialog';
import { RoleChip } from '../components/UserChips';
import { Profile, User } from '../models';

export interface UserListState {
  users: User[];
  isLoading: boolean;
  hasLoaded: boolean;
  lastFetched: Date;
  refetchUsers: () => Promise<void>;
}

interface UserListPageProps extends UserListState {
  profile: Profile
}

export default (props: UserListPageProps) => {
  const { users, isLoading, hasLoaded, lastFetched, refetchUsers } = props;
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const isAdmin = props.profile.role === 'admin';

  useEffect(() => {
    // Refetch in background on mount if last fetched more than 2 seconds ago
    if (lastFetched < new Date(new Date().getTime() - 1000 * 2)) {
      refetchUsers();
    }
  }, [lastFetched, refetchUsers]);

  const openDeleteUser = (userId: string, userEmail: string) => {
    setSelectedUserId(userId);
    setSelectedUserEmail(userEmail);
    setDeleteUserDialogOpen(true);
  }

  const closeModal = () => {
    setDeleteUserDialogOpen(false);
  }

  const renderDeleteButton = (userToDeleteId: string, userToDeleteEmail: string) => {
    if (isAdmin === false) {
      return (
        <Tooltip title="Only Admin users can perform this action" placement="right">
          <MuiLink component="button" style={{ textDecoration: "none" }} disabled>Delete</MuiLink>
        </Tooltip>
      )
    }

    if (props.profile._id === userToDeleteId) {
      return (
        <Tooltip title="Can't delete your own user" placement="right">
          <MuiLink component="button" style={{ textDecoration: "none" }} disabled>Delete</MuiLink>
        </Tooltip>
      )
    }

    return (
      <MuiLink component="button" style={{ textDecoration: "none" }} onClick={() => { openDeleteUser(userToDeleteId, userToDeleteEmail) }}>Delete</MuiLink>
    )
  }

  return (
    <DashboardPage
      title="Users"
      headerChildren={isAdmin ? (<Button component={RouterLink} to="/new-user" variant="contained">+ New User</Button>) : undefined}
      isLoading={isLoading && !hasLoaded}
    >
      <TableContainer>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((row) => (
              <TableRow
                key={row.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.fullName}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell><RoleChip role={row.role}/></TableCell>
                <TableCell>
                  {renderDeleteButton(row.id, row.email)}<br />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DeleteUserDialog open={deleteUserDialogOpen} userId={selectedUserId} userEmail={selectedUserEmail} onClose={closeModal} onSuccess={() => {
        closeModal();
        refetchUsers();
      }} />
    </DashboardPage >
  );
};
